/**
 * MyPlans — User's plan listing page with search, filter, sort, delete, and duplicate.
 *
 * Route: /user/plans (inside UserLayout)
 *
 * Features:
 *  - Search plans by title
 *  - Filter by status (All, Draft, Locked, Booked)
 *  - Sort by recent, oldest, title, budget
 *  - Plan cards showing title, location, budget, status, days/items count, trip type
 *  - Delete plan with confirmation dialog
 *  - Duplicate plan
 *  - Navigate to builder on card click
 *  - Pagination
 *  - "Create Plan" and "Browse Templates" CTAs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  useTheme,
  CircularProgress,
  Pagination,
  alpha,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Stack,
} from '@mui/material';
import {
  EditNote,
  Add,
  Explore,
  Search,
  Delete,
  ContentCopy,
  Lock,
  CalendarMonth,
  LocationOn,
  SortRounded,
  FilterList,
} from '@mui/icons-material';
import plansService from '../../services/plansService';
import { formatCurrency as formatMoney } from '../../utils/currency';
import { PageHeader, ButtonPill, EmptyState, panelSx, hoverCardSx } from '../ui';

// Status colors reference the theme palette (no hardcoded brand literals).
const statusColorFor = (theme, status) =>
  ({
    draft: theme.palette.warning.main,
    locked: theme.palette.success.main,
    booked: theme.palette.info.main,
  }[status] || theme.palette.grey[500]);

const STATUS_LABELS = {
  draft: 'Draft',
  locked: 'Finalized',
  booked: 'Booked',
};

const TRIP_TYPE_LABELS = {
  safari: 'Safari',
  beach: 'Beach',
  cultural: 'Cultural',
  adventure: 'Adventure',
  roadtrip: 'Road Trip',
  international: 'International',
  hiking: 'Hiking',
  camping: 'Camping',
  honeymoon: 'Honeymoon',
  corporate: 'Corporate',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const MyPlans = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total_pages: 1, total_count: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('');

  // UI state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const filters = { page, per_page: 12 };
      if (search.trim()) filters.search = search.trim();
      if (statusFilter) filters.status = statusFilter;
      if (sortBy) filters.sort = sortBy;

      const res = await plansService.getPlans(filters);
      setPlans(res?.plans ?? []);
      if (res?.pagination) {
        setPagination({
          total_pages: res.pagination.total_pages ?? 1,
          total_count: res.pagination.total_count ?? 0,
        });
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortBy]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortBy]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleDelete = async () => {
    if (!deleteDialog.plan) return;
    try {
      setActionLoading(true);
      await plansService.deletePlan(deleteDialog.plan.id);
      setSnackbar({ open: true, message: 'Plan deleted successfully', severity: 'success' });
      setDeleteDialog({ open: false, plan: null });
      fetchPlans();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to delete plan',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async (plan) => {
    try {
      setActionLoading(true);
      const res = await plansService.duplicatePlan(plan.id);
      if (res?.redirect_url) {
        setSnackbar({ open: true, message: 'Plan duplicated! Opening builder...', severity: 'success' });
        setTimeout(() => navigate(res.redirect_url), 600);
      } else {
        fetchPlans();
        setSnackbar({ open: true, message: 'Plan duplicated', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message ?? 'Failed to duplicate plan',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="My Plans"
        subtitle={
          pagination.total_count > 0
            ? `${pagination.total_count} ${pagination.total_count === 1 ? 'plan' : 'plans'}`
            : 'Your trip plans and customized itineraries'
        }
        action={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <ButtonPill
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/user/create-plan')}
            >
              Create Plan
            </ButtonPill>
            <ButtonPill
              variant="outlined"
              startIcon={<Explore />}
              onClick={() => navigate('/trips')}
            >
              Browse Templates
            </ButtonPill>
          </Box>
        }
      />

      {/* Search & Filter Bar */}
      <Box
        sx={{
          ...panelSx(theme),
          p: 2,
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Search plans..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FilterList sx={{ fontSize: 16 }} /> Status
            </Box>
          </InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status filter"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="locked">Finalized</MenuItem>
            <MenuItem value="booked">Booked</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SortRounded sx={{ fontSize: 16 }} /> Sort
            </Box>
          </InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort by order"
          >
            <MenuItem value="">Most Recent</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
            <MenuItem value="title">Title A-Z</MenuItem>
            <MenuItem value="budget_desc">Budget High-Low</MenuItem>
            <MenuItem value="budget_asc">Budget Low-High</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Loading */}
      {loading && plans.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Plans Grid */}
      {!loading && plans.length > 0 && (
        <Grid container spacing={3}>
          {plans.map((plan) => {
            const statusColor = statusColorFor(theme, plan.status);
            return (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card sx={{ ...hoverCardSx(theme), position: 'relative' }}>
                  <CardActionArea onClick={() => navigate(`/builder/${plan.id}`)}>
                    {/* Card header area */}
                    <Box
                      sx={{
                        position: 'relative',
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(statusColor, 0.08),
                        overflow: 'hidden',
                      }}
                    >
                      <EditNote
                        sx={{
                          fontSize: 56,
                          color: alpha(statusColor, 0.4),
                        }}
                      />
                      {/* Status badge */}
                      <Chip
                        icon={plan.status === 'locked' ? <Lock sx={{ fontSize: '14px !important' }} /> : undefined}
                        label={STATUS_LABELS[plan.status] || plan.status}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          textTransform: 'capitalize',
                          bgcolor: statusColor,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      />
                      {/* Trip type badge */}
                      {plan.trip_type && (
                        <Chip
                          label={TRIP_TYPE_LABELS[plan.trip_type] || plan.trip_type}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            bgcolor: 'rgba(255,255,255,0.9)',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>

                    <CardContent sx={{ p: 2, pb: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}
                        noWrap
                      >
                        {plan.title || 'Untitled Plan'}
                      </Typography>

                      {plan.location && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.5 }}
                        >
                          <LocationOn sx={{ fontSize: 14 }} />
                          {plan.location}
                        </Typography>
                      )}

                      {/* Stats row */}
                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                        {plan.days_count != null && (
                          <Chip
                            icon={<CalendarMonth sx={{ fontSize: '14px !important' }} />}
                            label={`${plan.days_count} day${plan.days_count !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 24 }}
                          />
                        )}
                        {plan.items_count != null && (
                          <Chip
                            label={`${plan.items_count} item${plan.items_count !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 24 }}
                          />
                        )}
                        {plan.source_trip_id && (
                          <Chip
                            label="Customized"
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 24,
                              borderColor: theme.palette.info.main,
                              color: theme.palette.info.main,
                            }}
                          />
                        )}
                      </Stack>

                      {/* Budget & dates */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{ fontWeight: 700 }}
                        >
                          {formatMoney(
                            plan.total_budget ?? plan.calculated_total,
                            plan.currency
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(plan.updated_at)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>

                  {/* Action buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      px: 1.5,
                      pb: 1.5,
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title="Duplicate plan">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(plan);
                        }}
                        disabled={actionLoading}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete plan">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteDialog({ open: true, plan });
                        }}
                        disabled={actionLoading}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Empty state */}
      {!loading && plans.length === 0 && (
        <EmptyState
          icon={<EditNote sx={{ fontSize: 64 }} />}
          title={search || statusFilter ? 'No plans match your filters' : 'No plans yet'}
          description={
            search || statusFilter
              ? 'Try adjusting your search or filters.'
              : 'Create a plan from scratch or customize a trip from the marketplace to build your own itinerary.'
          }
          action={
            search || statusFilter ? (
              <ButtonPill
                variant="outlined"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('');
                  setSortBy('');
                }}
              >
                Clear Filters
              </ButtonPill>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <ButtonPill
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/user/create-plan')}
                >
                  Create Plan
                </ButtonPill>
                <ButtonPill
                  variant="outlined"
                  startIcon={<Explore />}
                  onClick={() => navigate('/trips')}
                >
                  Browse Trips
                </ButtonPill>
              </Box>
            )
          }
        />
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination.total_pages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, plan: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Plan?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteDialog.plan?.title || 'this plan'}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, plan: null })}
            sx={{ borderRadius: 9999 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={actionLoading}
            sx={{ borderRadius: 9999 }}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyPlans;
